import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from './config';
import BrandHeader from './BrandHeader';

function Admin({ jwt }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserIsPowerUser, setNewUserIsPowerUser] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [toast, setToast] = useState({ message: '', type: '' });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE}/admin/users`, {
        headers: { Authorization: `Bearer ${jwt}` }
      });
      setUsers(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [jwt]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserEmail.trim()) {
      setToast({ message: 'Email is required', type: 'error' });
      return;
    }

    try {
      setAddingUser(true);
      setToast({ message: '', type: '' });
      const response = await axios.post(`${API_BASE}/admin/users`, {
        email: newUserEmail.trim().toLowerCase(),
        is_power_user: newUserIsPowerUser
      }, {
        headers: { Authorization: `Bearer ${jwt}` }
      });
      
      setUsers(prev => [...prev, response.data]);
      setNewUserEmail('');
      setNewUserIsPowerUser(false);
      setShowAddUser(false);
      setToast({ message: 'User added successfully!', type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.detail || err.message || 'Failed to add user', type: 'error' });
    } finally {
      setAddingUser(false);
    }
  };

  const handleTogglePowerUser = async (email, currentStatus) => {
    setToast({ message: '', type: '' });
    // Optimistically update UI
    setUsers(prev => prev.map(u => u.email === email ? { ...u, is_power_user: !currentStatus } : u));
    try {
      await axios.put(`${API_BASE}/admin/users/${encodeURIComponent(email)}`, {
        is_power_user: !currentStatus
      }, {
        headers: { Authorization: `Bearer ${jwt}` }
      });
      // No success toast for toggles
    } catch (err) {
      // Revert UI if error
      setUsers(prev => prev.map(u => u.email === email ? { ...u, is_power_user: currentStatus } : u));
      setToast({ message: err.response?.data?.detail || err.message || 'Failed to update user', type: 'error' });
    }
  };

  const handleDeleteUser = async (email) => {
    setDeletingUser(email);
    setToast({ message: '', type: '' });
    try {
      await axios.delete(`${API_BASE}/admin/users/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${jwt}` }
      });
      setUsers(prev => prev.filter(u => u.email !== email));
      setToast({ message: 'User deleted successfully!', type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.detail || err.message || 'Failed to delete user', type: 'error' });
    } finally {
      setDeletingUser(null);
      setConfirmDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mb-6"></div>
          <div className="text-xl font-semibold text-gray-700">Loading users...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fbff] py-4 md:py-8 box-border">
      <div className="max-w-4xl mx-auto px-4">
        <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_24px_70px_rgba(31,41,55,0.10)] w-full box-border">
          <BrandHeader
            title="Admin Panel"
            actions={(
              <button
                onClick={() => setShowAddUser(true)}
                className="rounded-2xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white shadow-sm backdrop-blur transition-colors hover:bg-white/20"
              >
                Add User
              </button>
            )}
          />
          
          <div className="pb-6 md:p-8 w-full box-border">
            {toast.message && (
              <div className={`mb-4 w-full px-4 py-2 rounded text-center font-semibold ${toast.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-700 border border-red-300'}`}>
                {toast.message}
              </div>
            )}
            
            {loading && <div className="text-gray-500 text-center py-8">Loading users...</div>}
            {error && <div className="text-red-600 mb-4 text-center">{error}</div>}
            {!loading && !error && users.length === 0 && (
              <div className="text-gray-500 text-center py-8">
                <span className="material-icons text-4xl mb-4 text-gray-300">people</span>
                <p className="text-lg">No users found</p>
                <p className="text-sm">Add your first user to get started</p>
              </div>
            )}
            {!loading && !error && users.length > 0 && (
              <div className="w-full max-w-2xl mx-auto space-y-4">
                {users.map((user) => (
                  <div key={user.email} className="border rounded-lg p-4 bg-white shadow-sm relative flex flex-col">
                    {/* User info and actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div>
                          <div className="font-medium text-gray-900">{user.email}</div>
                          <div className="text-sm text-gray-500">
                            {user.is_environment_power_user 
                              ? 'Environment Power User' 
                              : (user.is_power_user ? 'Power User' : 'Regular User')
                            }
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {!user.is_environment_power_user && (
                          <button
                            onClick={() => handleTogglePowerUser(user.email, user.is_power_user)}
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-600 hover:text-gray-800 shadow-sm transition-colors focus:outline-none"
                            title={user.is_power_user ? "Remove Power User" : "Make Power User"}
                            style={{ cursor: 'pointer', lineHeight: 1 }}
                          >
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              {user.is_power_user ? (
                                // Arrow down for removing power
                                <path d="M10 3L10 17M10 17L6 13M10 17L14 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              ) : (
                                // Arrow up for adding power
                                <path d="M10 17L10 3M10 3L6 7M10 3L14 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              )}
                            </svg>
                          </button>
                        )}
                        {!user.is_environment_power_user && (
                          <button
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 hover:text-red-800 shadow-sm transition-colors focus:outline-none"
                            title="Delete user"
                            onClick={() => setConfirmDelete(user.email)}
                            disabled={deletingUser === user.email}
                            style={{ cursor: 'pointer', lineHeight: 1 }}
                          >
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="10" cy="10" r="9" fill="none"/>
                              <path d="M6 6L14 14M14 6L6 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                          </button>
                        )}
                        {user.is_environment_power_user && (
                          <span className="px-3 py-1 rounded text-sm font-medium bg-blue-100 text-blue-800">
                            Power User
                          </span>
                        )}
                      </div>
                    </div>
                    

                    
                    {/* Confirm delete modal */}
                    {confirmDelete === user.email && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                        <div className="bg-white rounded-lg shadow-lg p-6 max-w-xs w-full flex flex-col items-center">
                          <div className="text-lg font-bold mb-4 text-gray-800">Delete this user?</div>
                          <div className="mb-6 text-gray-600 text-center">Are you sure you want to remove {user.email}?</div>
                          <div className="flex gap-3 w-full">
                            <button
                              className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                              onClick={() => setConfirmDelete(null)}
                            >
                              No
                            </button>
                            <button
                              className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
                              onClick={() => handleDeleteUser(user.email)}
                              disabled={deletingUser === user.email}
                            >
                              {deletingUser === user.email ? 'Deleting...' : 'Yes, Delete'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Add New User</h3>
            </div>
            
            <form onSubmit={handleAddUser} className="px-6 py-4">
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="user@example.com"
                  required
                />
              </div>
              
              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newUserIsPowerUser}
                    onChange={(e) => setNewUserIsPowerUser(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Power User (Admin privileges)</span>
                </label>
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddUser(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingUser}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {addingUser ? 'Adding...' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
